/**
 * Thresholds and labels the Stock screens share.
 *
 * These are display constants only. The status a row carries is computed server side from
 * the same numbers, so nothing here re-derives `critical` or `low`; the bar length and the
 * chip labels use them for presentation.
 */
import type { ProductFamily, StockStatus } from '@elutide/shared';

export const CRITICAL_DAYS = 7;
export const LOW_DAYS = 21;

export const STATUS_LABEL: Record<StockStatus, string> = {
  critical: 'Critical',
  low: 'Low',
  ok: 'OK',
  out: 'Out',
  suppressed: 'Hidden',
};

/**
 * The colour band on the left edge of a family.
 *
 * Tokens only, and it is never the only signal: the family name is rendered beside it and
 * the row carries the SKU. A band with no name would be a colour quiz.
 */
export const FAMILY_BAND: Record<ProductFamily, string> = {
  glp: 'var(--teal)',
  glow: 'var(--copper)',
  klow: 'var(--burgundy-tint)',
  bpc: 'var(--ok)',
  tb: 'var(--warn)',
  reconstitution: 'var(--teal-dim)',
  other: 'var(--text-muted)',
};

export const FAMILY_LABEL: Record<ProductFamily, string> = {
  glp: 'GLP',
  glow: 'GLOW',
  klow: 'KLOW',
  bpc: 'BPC-157',
  tb: 'TB-500',
  reconstitution: 'Reconstitution',
  other: 'Other',
};

export const ADJUST_REASONS: { value: string; label: string }[] = [
  { value: 'count_correction', label: 'Count correction' },
  { value: 'breakage', label: 'Breakage' },
  { value: 'qc_sample', label: 'QC sample used' },
  { value: 'giveaway_unlogged', label: 'Giveaway or comp not logged' },
  { value: 'prior_miscount', label: 'Prior miscount' },
  { value: 'receiving_unlogged', label: 'Receiving not logged' },
  { value: 'loss_or_theft', label: 'Loss or theft' },
  { value: 'found', label: 'Found' },
  { value: 'sync_variance', label: 'Sync variance' },
  { value: 'other', label: 'Other' },
];

/** The packer's daily ceiling per SKU. Mirrors PACKER_DAILY_UNIT_CAP in the API. */
export const PACKER_DAILY_UNIT_CAP = 10;

/**
 * Series colours for the cost by supplier chart.
 *
 * Token variables only, so a rebrand moves them and nothing here holds a hex value. Five is
 * enough: past that the chart is unreadable anyway and the legend carries the numbers.
 */
const SERIES_TOKENS = ['--teal', '--copper', '--ok', '--warn', '--burgundy-tint'];

export function seriesColor(index: number): string {
  return `var(${SERIES_TOKENS[index % SERIES_TOKENS.length] ?? '--teal'})`;
}
